import React, { useRef } from "react";
import axios from "axios";
import * as rax from "retry-axios";
import { batch } from "react-redux";

import {
  FETCH_PAGES,
  MERGE_PAGES,
  UPDATE_PAGE,
  RESTORE_PAGES,
  CHANGE_IMAGE,
  CHANGE_VIDEO,
  CHANGE_AUDIO,
  MOVE_AUDIO,
  ADD_AUDIO,
  DELETE_AUDIO,
  CHANGE_CARD_IMAGE,
  CHANGE_CARD_AUDIO,
  ADD_CARD,
  DELETE_CARD,
  OPEN_EDITOR,
  REMOVE_IMAGE,
  REMOVE_VIDEO,
  REMOVE_AUDIO,
  REMOVE_CARD_IMAGE,
  REMOVE_CARD_AUDIO,
  CHANGE_TEXT,
  CHANGE_REMARK_TEXT,
  CHANGE_AUDIO_HIGHLIGHT_ENABLED,
  CHANGE_CONTENT_TEXT,
  CHANGE_REMARK_CONTENT_TEXT,
  CHANGE_CARD_TEXT,
  CHANGE_CARD_REMARK_TEXT,
  CHANGE_TITLE,
  CHANGE_TEACHING_NOTES,
  CHANGE_REMARK_TEACHING_NOTES,
  UPDATE_SECTION,
  SET_ERROR,
  UPDATE_UNIT,
  UPDATE_BOOK,
  CLOSE_EDITOR,
  ADD_IMAGE,
  ADD_VIDEO,
  CHANGE_VIDEO_STATUS,
  ADD_FILE,
  UPDATE_RESOURCE,
  LOADING_PAGES,
  MOVE_CARD,
  UPDATE_TRAINING,
  FETCH_PAGES_NUMBER,
  UPDATE_FOLDER,
  UPDATE_VIDEOLIST,
  SET_LOAD,
  CLOSE_LOAD,
  ADD_PLAYGROUND,
  DELETE_PLAYGROUND,
  ADD_PLAYGROUNDLEVEL,
  CHANGE_INSTRUCTION_TEXT,
  CHANGE_INSTRUCTION_IMAGE,
  REMOVE_INSTRUCTION_IMAGE
} from "./type";

import mediaServerAddress from "../config/mediaServerAddress";
import { rollbar } from "../loggers";

// media server for video

// fetch all the pages in section
export const fetchPages = (options, callback) => async (dispatch, getStore) => {
  dispatch({ type: LOADING_PAGES, payload: {} });

  let query = ``;

  if (options.pageType !== undefined)
    query = `${query}&pageType=${options.pageType}`;
  if (options.sectionId !== undefined)
    query = `${query}&sectionId=${options.sectionId}`;
  if (options.unitId !== undefined) query = `${query}&unitId=${options.unitId}`;
  if (options.bookId !== undefined) query = `${query}&bookId=${options.bookId}`;
  if (options.bookTypeId !== undefined)
    query = `${query}&bookTypeId=${options.bookTypeId}`;
  if (options.booksetId !== undefined)
    query = `${query}&booksetId=${options.booksetId}`;
  if (options.resourceId !== undefined)
    query = `${query}&resourceId=${options.resourceId}`;
  if (options.isHidden !== undefined)
    query = `${query}&isHidden=${options.isHidden.toString()}`;
  if (options.isTrash !== undefined)
    query = `${query}&isTrash=${options.isTrash.toString()}`;

  if (options.folderId !== undefined)
    query = `${query}&folderId=${options.folderId}`;

  if (options.videoFolderId !== undefined)
    query = `${query}&videoFolderId=${options.videoFolderId}`;

  if (options.videoListId !== undefined)
    query = `${query}&videoListId=${options.videoListId}`;

  const route = `page${query ? `?${query.slice(1)}` : ``}`;

  const {
    data: { pageMap }
  } = await axios.get(route);

  dispatch({
    type: options.merge ? MERGE_PAGES : FETCH_PAGES,
    payload: { pageMap }
  });

  if (callback) callback();
};

// fetch quiz pages based on bookType (randomly select 10 pages from each book)
// options.merge: if true, merge new pages with existing ones (for lazy loading)
// options.limit: limit number of pages to fetch (e.g., 60 for one book)
// options.bookId: specific book to fetch pages from (for lazy loading next book)
// options.isEnrolledStudent: whether the student is enrolled (for filtering books)
export const fetchQuizPages =
  (options, callback) => async (dispatch, getStore) => {
    dispatch({ type: LOADING_PAGES, payload: {} });

    let query = ``;

    if (options.bookTypeId !== undefined)
      query = `${query}&bookTypeId=${options.bookTypeId}`;
    if (options.startBookId !== undefined)
      query = `${query}&startBookId=${options.startBookId}`;
    if (options.bookId !== undefined)
      query = `${query}&bookId=${options.bookId}`;
    if (options.limit !== undefined)
      query = `${query}&limit=${options.limit}`;
    if (options.isEnrolledStudent !== undefined)
      query = `${query}&isEnrolledStudent=${options.isEnrolledStudent}`;

    const route = `quiz/pages${query ? `?${query.slice(1)}` : ``}`;

    const {
      data: { pageMap, bookMap }
    } = await axios.get(route);

    batch(() => {
      dispatch({
        type: options.merge ? MERGE_PAGES : FETCH_PAGES,
        payload: { pageMap }
      });

      // Update bookMap if provided by backend
      if (bookMap) {
        Object.values(bookMap).forEach((book) => {
          dispatch({
            type: UPDATE_BOOK,
            payload: { book }
          });
        });
      }
    });

    if (callback) callback();
  };

// create one page
export const createPage =
  (formProps, callback) => async (dispatch, getStore) => {
    const {
      data: { page }
    } = await axios.post(`page`, formProps);

    let res;
    switch (formProps.pageType) {
      case `teachingAssistant`:
        res = await axios.patch(`section/${formProps.sectionId}`, {
          patch: {
            action: `ADD_TEACHING_ASSISTANT`,
            data: { pageId: page._id, position: formProps.position }
          }
        });

        batch(() => {
          dispatch({ type: UPDATE_PAGE, payload: { page } });

          dispatch({
            type: UPDATE_SECTION,
            payload: { section: res.data?.section }
          });
        });
        break;

      case `lessonPlan`:
        res = await axios.patch(`unit/${formProps.unitId}`, {
          patch: {
            action: `ADD_LESSON_PLAN`,
            data: { pageId: page._id }
          }
        });

        batch(() => {
          dispatch({ type: UPDATE_PAGE, payload: { page } });

          dispatch({
            type: UPDATE_UNIT,
            payload: { unit: res.data?.unit }
          });
        });
        break;

      case `teachingVideo`:
        res = await axios.patch(`unit/${formProps.unitId}`, {
          patch: {
            action: `ADD_TEACHING_VIDEO`,
            data: {
              pageId: page._id,
              teachingVideoId: formProps.teachingVideoId
            }
          }
        });

        batch(() => {
          dispatch({ type: UPDATE_PAGE, payload: { page } });

          dispatch({
            type: UPDATE_UNIT,
            payload: { unit: res.data?.unit }
          });
        });
        break;

      case `downloadPage`:
        const {
          data: { folder }
        } = await axios.patch(`category/folder/${formProps.folderId}`, {
          patch: {
            action: `addDownloadPage`,
            data: {
              categoryId: formProps.categoryId,
              downloadPageId: page._id
            }
          }
        });

        batch(() => {
          dispatch({ type: UPDATE_PAGE, payload: { page } });

          dispatch({
            type: UPDATE_FOLDER,
            payload: { folder }
          });
        });
        break;

      case `videosPage`:
        const {
          data: { videoList }
        } = await axios.patch(`category/folder/${formProps.folderId}`, {
          patch: {
            action: `addVideosPage`,
            data: {
              categoryId: formProps.categoryId,
              folderId: formProps.folderId,
              downloadPageId: page._id
            }
          }
        });

        batch(() => {
          dispatch({ type: UPDATE_PAGE, payload: { page } });

          dispatch({
            type: UPDATE_VIDEOLIST,
            payload: { videoList }
          });
        });
        break;

      case `trainingVideo`:
        res = await axios.patch(`training/${formProps.trainingId}`, {
          patch: {
            action: `ADD_TRAINING_VIDEO`,
            data: {
              pageId: page._id
            }
          }
        });

        batch(() => {
          dispatch({ type: UPDATE_PAGE, payload: { page } });

          dispatch({
            type: UPDATE_TRAINING,
            payload: { training: res.data?.training }
          });
        });
        break;

      default:
        await axios.delete(`page/${page._id}`);
        return dispatch({
          type: SET_ERROR,
          payload: `Unprocessable page type`
        });
    }

    if (callback) callback(page);
  };

// save page
export const savePage =
  (formProps, callback, existFileSrcs, existImageSrcs, options = {}) =>
  async (dispatch, getStore) => {
    try {
      const pageMap = getStore().page.pageMap;
      const editor = getStore().page.editor;

      // Auto-hide/unhide speaking style 0 based on audio presence
      if (editor?.styleType === "speaking" && editor?.style === 0) {
        const hasAudio = !!editor?.data?.audios?.[0]?._audio?.src;
        if (!hasAudio) {
          const confirmed = window.confirm(
            "No audio uploaded. This page will be automatically hidden from students.\n\nSave anyway?"
          );
          if (!confirmed) {
            dispatch({ type: CLOSE_LOAD });
            return;
          }
          formProps.isHidden = true;
        }
      }

      const newPage = {};

      const progressMap = {}; // { index: percent }
      const { onProgress } = options;

      newPage.title = formProps.title;
      newPage.isHidden = formProps.isHidden;
      newPage.level = formProps.level; //page difficulty only for quiz
      newPage.skill = formProps.skill; //skill set array for quiz
      newPage.isAutoPlayMedia = formProps.isAutoPlayMedia;
      newPage.data = {};
      // Reading Style 10 highlight toggle. Only persist for reading:10 pages
      // — other styles never read this field, no need to clutter their docs.
      if (editor.styleType === "reading" && editor.style === 10) {
        newPage.data.audioHighlightEnabled =
          formProps.audioHighlightEnabled !== undefined
            ? !!formProps.audioHighlightEnabled
            : editor.data?.audioHighlightEnabled !== false;
      }
      newPage.data.teachingNotes = formProps.teachingNotes;
      newPage.data.remarkTeachingNotes = formProps.remarkTeachingNotes;
      newPage.data.questionExplanation = formProps.questionExplanation;
      newPage.data.correctAnswer = formProps.correctAnswer;

      // Handle instruction field (text or image)
      newPage.data.instruction = {
        text: formProps.instructionText || "",
        mode: formProps.instructionMode || 0
      };

      newPage.data._files = editor.data._files;

      newPage.data.playgroundLevel = editor.data.playgroundLevel;

      newPage.data.playgrounds = editor.data.playgrounds;

      // Always thread pdfSource through save so:
      //  - present (object)         → preserved on server (panel keeps working)
      //  - explicitly null          → user clicked Detach; clear on server
      //  - undefined (legacy pages) → field omitted, no change
      if (editor.data.pdfSource !== undefined) {
        newPage.data.pdfSource = editor.data.pdfSource;
      }

      // walk through the text fields in editor and load the value from form
      newPage.data.texts =
        editor.data.texts &&
        editor.data.texts.map((text, index) => {
          return { ...text, value: formProps[`text_${index}`] };
        });

      // walk through the remarks fields in editor and load the value from form
      newPage.data.remarks =
        editor.data.remarks &&
        editor.data.remarks.map((remark, index) => {
          return { ...remark, value: formProps[`remark_${index}`] };
        });

      // Collect contentTexts from form (contentTexts[0].value, contentTexts[1].value, etc.)
      newPage.data.contentTexts =
        editor.data.contentTexts &&
        editor.data.contentTexts.map((contentText, index) => {
          const value =
            formProps.contentTexts?.[index]?.value ?? contentText.value;

          return {
            ...contentText,
            value
          };
        });

      // Collect contentRemarks (styled rich text) from form (contentRemark_0, contentRemark_1, etc.)
      // IMPORTANT: Create contentRemarks array from formProps even if editor.data.contentRemarks doesn't exist
      // This ensures styled text edits from dialog are saved
      if (editor.data.contentTexts && editor.data.contentTexts.length > 0) {
        newPage.data.contentRemarks = editor.data.contentTexts.map(
          (contentText, index) => {
            // If editor already has contentRemarks, use existing data
            const existingRemark = editor.data.contentRemarks?.[index] || {};

            // Get the styled text value from form (edited via StyledTextareaFormDialog)
            const remarkValue = formProps[`contentRemark_${index}`];

            return {
              ...existingRemark,
              value:
                remarkValue !== undefined ? remarkValue : existingRemark.value
            };
          }
        );
      } else if (editor.data.contentRemarks) {
        // Fallback: Use existing contentRemarks if contentTexts doesn't exist
        newPage.data.contentRemarks = editor.data.contentRemarks.map(
          (contentRemark, index) => {
            return {
              ...contentRemark,
              value: formProps[`contentRemark_${index}`]
            };
          }
        );
      }

      // Collect contentImages from form - these will be uploaded later in the images section
      // For now, just preserve the structure and mark files for upload
      if (editor.data.contentImages) {
        newPage.data.contentImages = editor.data.contentImages.map(
          (contentImage, index) => {
            const fieldName = `contentImages[${index}]`;
            const imageFile = formProps[fieldName];
            console.log(`Collecting contentImage ${index}:`, {
              fieldName,
              hasFile: !!imageFile,
              hasExistingImage: !!contentImage._image
            });
            if (imageFile) {
              // Mark this image for upload
              return {
                ...contentImage,
                _image: {
                  ...(contentImage._image || {}),
                  file: imageFile
                }
              };
            }
            return contentImage;
          }
        );
      }

      // Upload blob images in remarkTeachingNotes and questionExplanation to S3
      const uploadBlobImagesInRichText = async (richTextContent) => {
        if (!richTextContent || typeof richTextContent !== "string") {
          return richTextContent;
        }

        // Skip non-JSON content (plain text fields don't need blob image processing)
        if (!richTextContent.trimStart().startsWith("{")) {
          return richTextContent;
        }

        try {
          const contentState = JSON.parse(richTextContent);

          if (!contentState.entityMap) {
            return richTextContent;
          }

          const blobUploads = [];
          const blobUrlToEntityKey = {};

          // Find all blob URLs in entities
          Object.entries(contentState.entityMap).forEach(([key, entity]) => {
            if (
              entity.type === "IMAGE" &&
              entity.data?.src?.startsWith("blob:")
            ) {
              blobUrlToEntityKey[entity.data.src] = key;
              blobUploads.push(entity.data.src);
            }
          });

          if (blobUploads.length === 0) {
            return richTextContent;
          }

          console.log(
            `Found ${blobUploads.length} blob images to upload to S3`
          );

          // Convert blob URLs to actual files and upload
          const uploadPromises = blobUploads.map(async (blobUrl) => {
            try {
              const response = await fetch(blobUrl);
              const blob = await response.blob();

              const fd = new FormData();
              fd.append("image", blob, "rich-text-image.png");

              const {
                data: { image }
              } = await mediaServerAddress.post("image", fd);

              console.log(`Uploaded blob image to S3:`, image.src);
              return { blobUrl, s3Url: image.src };
            } catch (error) {
              console.error(`Failed to upload blob image:`, error);
              return { blobUrl, s3Url: null };
            }
          });

          const uploadResults = await Promise.all(uploadPromises);

          // Replace blob URLs with S3 URLs in entityMap
          uploadResults.forEach(({ blobUrl, s3Url }) => {
            if (s3Url) {
              const entityKey = blobUrlToEntityKey[blobUrl];
              if (contentState.entityMap[entityKey]) {
                contentState.entityMap[entityKey].data.src = s3Url;
              }
            }
          });

          return JSON.stringify(contentState);
        } catch (error) {
          console.error("Failed to process rich text images:", error);
          return richTextContent;
        }
      };

      // Upload blob images in remarkTeachingNotes
      if (newPage.data.remarkTeachingNotes) {
        newPage.data.remarkTeachingNotes = await uploadBlobImagesInRichText(
          newPage.data.remarkTeachingNotes
        );
      }

      // Upload blob images in questionExplanation
      if (newPage.data.questionExplanation) {
        newPage.data.questionExplanation = await uploadBlobImagesInRichText(
          newPage.data.questionExplanation
        );
      }

      // Upload blob images in remarks array (remark_0, remark_1, etc.)
      if (newPage.data.remarks && Array.isArray(newPage.data.remarks)) {
        newPage.data.remarks = await Promise.all(
          newPage.data.remarks.map(async (remark) => {
            if (remark && remark.value) {
              return {
                ...remark,
                value: await uploadBlobImagesInRichText(remark.value)
              };
            }
            return remark;
          })
        );
      }

      // Upload blob images in contentRemarks array (contentRemark_0, contentRemark_1, etc.)
      if (
        newPage.data.contentRemarks &&
        Array.isArray(newPage.data.contentRemarks)
      ) {
        newPage.data.contentRemarks = await Promise.all(
          newPage.data.contentRemarks.map(async (contentRemark) => {
            if (contentRemark && contentRemark.value) {
              return {
                ...contentRemark,
                value: await uploadBlobImagesInRichText(contentRemark.value)
              };
            }
            return contentRemark;
          })
        );
      }

      // Auto-sync between remarks and texts for Style 10 (reading/word)
      // This ensures speech recognition has the correct plain text to compare against
      // PRIORITY: remark_0 (rich text) is the source of truth, text_0 (plain text) is derived
      if (
        (editor.styleType === "reading" || editor.styleType === "word") &&
        editor.style === 10
      ) {
        const extractPlainText = (draftContent) => {
          if (!draftContent) return "";

          try {
            // If it's a string, try to parse it as JSON first
            if (typeof draftContent === "string") {
              try {
                const parsed = JSON.parse(draftContent);
                // If parsing succeeds and it has blocks, extract from blocks
                if (parsed.blocks && Array.isArray(parsed.blocks)) {
                  return parsed.blocks
                    .map((block) => block.text)
                    .join("\n")
                    .trim();
                }
                // Otherwise return the original string
                return draftContent;
              } catch (e) {
                // If parsing fails, it's a plain string, return it
                return draftContent;
              }
            }

            // If it's a Draft.js contentState object
            if (draftContent.blocks && Array.isArray(draftContent.blocks)) {
              return draftContent.blocks
                .map((block) => block.text)
                .join("\n")
                .trim();
            }

            return "";
          } catch (error) {
            console.error("Error extracting plain text:", error);
            return "";
          }
        };

        // NEW APPROACH: Prioritize remark_0 (rich text) as source of truth
        // Extract plain text from remark_0 and save to text_0
        if (
          newPage.data.remarks &&
          newPage.data.remarks[0] &&
          newPage.data.remarks[0].value
        ) {
          const plainText = extractPlainText(newPage.data.remarks[0].value);

          // Ensure texts[0] exists and update its value with plain text from remark_0
          if (!newPage.data.texts) {
            newPage.data.texts = [];
          }
          if (newPage.data.texts[0]) {
            newPage.data.texts[0].value = plainText;
          } else {
            // If texts[0] doesn't exist, create it
            newPage.data.texts[0] = {
              value: plainText,
              exist: true,
              position: {}
            };
          }
        }
        // Fallback: If no remark_0 but text_0 exists (shouldn't happen with new UI)
        else if (
          newPage.data.texts &&
          newPage.data.texts[0] &&
          newPage.data.texts[0].value
        ) {
          // Keep text_0 as is, no remark needed
          console.warn(
            "Reading Style 10: text_0 exists but remark_0 is missing"
          );
        }
      }

      // Speaking style: ensure transcript + mode fields are saved
      if (editor.styleType === "speaking") {
        if (!newPage.data.texts) newPage.data.texts = [];
        if (!newPage.data.texts[0]) {
          newPage.data.texts[0] = {
            value: formProps.text_0 || "",
            exist: true,
            position: {}
          };
        } else {
          newPage.data.texts[0].value = formProps.text_0 || "";
        }
        newPage.data.isQuiz = !!formProps.isQuiz;
        newPage.data.timeLimit = formProps.timeLimit
          ? parseInt(formProps.timeLimit, 10)
          : 0;
        // Style 1 (Read Aloud) only
        if (editor.style === 1) {
          newPage.data.prepTime = formProps.prepTime
            ? parseInt(formProps.prepTime, 10)
            : 0;
        }
      }

      // walk through the answers of the page
      newPage.data.answers = [];
      Object.keys(formProps).forEach((key) => {
        if (key.indexOf(`answer_`) !== -1) {
          if (formProps[key] !== undefined) {
            newPage.data.answers[key.split("_")[1]] =
              formProps[key] === true
                ? 1
                : formProps[key] === false
                  ? 0
                  : parseFloat(formProps[key]);
          }
        }
      });

      const uselessImages = {};
      const uselessVideos = {};
      const uselessAudios = {};

      // walk through the origin page and store the image data
      pageMap[editor._id].data.images &&
        pageMap[editor._id].data.images.forEach((image) => {
          if (image?._image?._id) uselessImages[image._image._id] = true;
        });

      // walk through the origin page and store the video data
      pageMap[editor._id].data.videos &&
        pageMap[editor._id].data.videos.forEach((video) => {
          if (video?._video?._id) uselessVideos[video._video._id] = true;
        });

      // walk through the origin page and store the audio data
      pageMap[editor._id].data.audios &&
        pageMap[editor._id].data.audios.forEach((audio) => {
          if (audio?._audio?._id) uselessAudios[audio._audio._id] = true;
        });

      // walk through the origin page and store the card data
      pageMap[editor._id].data.cards &&
        pageMap[editor._id].data.cards.forEach((card) => {
          if (card) {
            if (card.image?._image?._id)
              uselessImages[card.image._image._id] = true;
            if (card.audio?._audio?._id)
              uselessAudios[card.audio._audio._id] = true;
            if (card.audios) {
              card.audios.forEach((audio) => {
                if (audio?._audio?._id) uselessAudios[audio._audio._id] = true;
              });
            }
            if (card.images) {
              card.images.forEach((image) => {
                if (image?._image?._id) uselessImages[image._image._id] = true;
              });
            }
          }
        });

      newPage.data.images =
        editor.data.images &&
        (await Promise.all(
          // walk through the image fields in editor and load the value from form
          editor.data.images.map(async (element, index) => {
            if (element === null) return null;

            const { _image } = element;

            // image still in use, remove form useless list
            if (_image?._id && uselessImages[_image._id])
              delete uselessImages[_image._id];

            // Check if image needs to be uploaded (from editor state or formProps)
            const imageFile = _image?.file || formProps[`image_${index}`];

            // if image is uploaded
            if (imageFile) {
              // if old image exist, delete old image, mark useless
              if (_image?._id) uselessImages[_image._id] = true;

              const fd = new FormData();

              fd.append("image", imageFile);

              try {
                // upload new image
                const {
                  data: { image }
                } = await mediaServerAddress.post(`image`, fd, {
                  onUploadProgress: (progressEvent) => {
                    const percent = Math.round(
                      (progressEvent.loaded * 100) / progressEvent.total
                    );

                    // 記錄該圖片的進度
                    progressMap[index] = percent;

                    // 算整體進度
                    const values = Object.values(progressMap);
                    const avgProgress = Math.round(
                      values.reduce((a, b) => a + b, 0) / values.length
                    );

                    onProgress?.({
                      loadMessage: `Uploading image...`,
                      progress: avgProgress
                    });
                  }
                });

                // ✅ 驗證回傳的影像是否為物件
                if (!image || typeof image !== "object") {
                  console.error("無效的影像物件結構:", image);
                  return element; // 失敗時返回原元素
                }

                // ✅ 確保 _image 不是字串
                if (typeof image === "string") {
                  console.error("影像上傳回傳字串而非物件:", image);
                  return element;
                }

                // ✅ 驗證必要欄位
                if (!image._id || !image.src) {
                  console.error("影像物件缺少必要欄位:", image);
                  return element;
                }

                return {
                  ...element,
                  _image: image,
                  imageMode:
                    formProps[`imageMode_${index}`] ||
                    element.imageMode ||
                    "circle",
                  exist: formProps[`imageExist_${index}`] !== false
                };
              } catch (uploadError) {
                console.error(
                  `上傳索引 ${index} 的影像時發生錯誤:`,
                  uploadError
                );
                return element; // 失敗時返回原元素
              }
            }

            // ✅ 如果沒有上傳新影像，驗證原影像結構
            if (element && _image) {
              // PDF bulk-create stores `_image` as a plain ObjectId string
              // (initialData passes the upload's imageId as a string). The
              // server keeps that link untouched; if we save the page back
              // without preserving it, the image is dropped. Treat a string
              // _image as a valid reference and pass it through. Only an
              // OBJECT _image whose `src` is a blob: URL is the genuine
              // "needs re-upload" case (e.g. previewed but never uploaded).
              if (typeof _image === "string") {
                return {
                  ...element,
                  _image,
                  imageMode:
                    formProps[`imageMode_${index}`] ||
                    element.imageMode ||
                    "circle",
                  exist: formProps[`imageExist_${index}`] !== false
                };
              }

              if (typeof _image === "object" && _image.src?.startsWith("blob:")) {
                console.warn(
                  "警告：偵測到 blob URL，需要重新上傳:",
                  _image.src
                );
                return null; // 不保存 blob URL
              }

              // ✅ 確保 _image 是有效物件
              if (typeof _image === "object" && _image._id && _image.src) {
                return {
                  ...element,
                  _image: _image,
                  imageMode:
                    formProps[`imageMode_${index}`] ||
                    element.imageMode ||
                    "circle",
                  exist: formProps[`imageExist_${index}`] !== false
                };
              }

              // Last resort: object with _id but no `src` yet (server may
              // not have populated it on this round-trip). Preserve the
              // link so we don't accidentally drop a valid image.
              if (typeof _image === "object" && _image._id) {
                return {
                  ...element,
                  _image,
                  imageMode:
                    formProps[`imageMode_${index}`] ||
                    element.imageMode ||
                    "circle",
                  exist: formProps[`imageExist_${index}`] !== false
                };
              }

              console.warn("影像物件結構不完整 (缺少 _id 或 src):", _image);
              return null;
            }

            // Update exist field even if no image changes
            return {
              ...element,
              exist: formProps[`imageExist_${index}`] !== false
            };
          })
        ));

      // clean up deleted images
      newPage.data.images =
        newPage.data.images && newPage.data.images.filter((element) => element);

      // ================================================================
      // Handle contentImages upload (for QuizStyle15 multiple content feature)
      if (
        newPage.data.contentImages &&
        Array.isArray(newPage.data.contentImages)
      ) {
        newPage.data.contentImages = await Promise.all(
          newPage.data.contentImages.map(async (contentImage, index) => {
            if (!contentImage) return null;

            const { _image } = contentImage;

            // Check if there's a new image file to upload
            const imageFile =
              _image?.file || formProps[`contentImages[${index}]`];

            if (imageFile) {
              const fd = new FormData();
              fd.append("image", imageFile);

              try {
                // Upload new image
                const {
                  data: { image }
                } = await mediaServerAddress.post(`image`, fd, {
                  onUploadProgress: (progressEvent) => {
                    const percent = Math.round(
                      (progressEvent.loaded * 100) / progressEvent.total
                    );
                    onProgress?.({
                      loadMessage: `Uploading content image ${index + 1}...`,
                      progress: percent
                    });
                  }
                });

                // Validate returned image object
                if (
                  !image ||
                  typeof image !== "object" ||
                  !image._id ||
                  !image.src
                ) {
                  console.error("Invalid content image object:", image);
                  return contentImage; // Return original on error
                }

                return {
                  ...contentImage,
                  _image: image,
                  exist: true
                };
              } catch (uploadError) {
                console.error(
                  `Error uploading content image ${index}:`,
                  uploadError
                );
                return contentImage; // Return original on error
              }
            }

            // Validate existing image (no new upload)
            if (_image) {
              // String _image → plain ObjectId reference (PDF bulk-create
              // path / non-populated server response). Preserve as-is so
              // the image isn't dropped on save.
              if (typeof _image === "string") return contentImage;

              // Only true blob URLs (uploaded but not persisted) are
              // considered "needs re-upload" → drop to avoid saving a
              // broken reference.
              if (typeof _image === "object" && _image.src?.startsWith("blob:")) {
                console.warn(
                  "Warning: Detected blob URL in contentImage, needs re-upload:",
                  _image.src
                );
                return null;
              }

              // Ensure _image is valid object
              if (typeof _image === "object" && _image._id && _image.src) {
                return contentImage;
              }

              // Object with _id but no src — preserve the link (server may
              // not have populated src in this fetch).
              if (typeof _image === "object" && _image._id) return contentImage;

              console.warn(
                "Content image object structure incomplete:",
                _image
              );
              return null;
            }

            return contentImage;
          })
        );

        // Clean up deleted content images
        newPage.data.contentImages = newPage.data.contentImages.filter(
          (element) => element
        );
      }

      // ================================================================
      // Handle instruction image upload
      // Check if there's a new file to upload (from editor state or formProps)
      const instructionImageFile =
        editor.data.instruction?.image?._image?.file ||
        formProps.instructionImage;

      console.log("Instruction image upload check:", {
        hasInstructionImageFile: !!instructionImageFile,
        hasEditorFile: !!editor.data.instruction?.image?._image?.file,
        hasFormPropsFile: !!formProps.instructionImage,
        editorSrc: editor.data.instruction?.image?._image?.src
      });

      if (instructionImageFile) {
        const fd = new FormData();
        fd.append("image", instructionImageFile);

        try {
          const {
            data: { image }
          } = await mediaServerAddress.post("image", fd, {
            onUploadProgress: (progressEvent) => {
              const percent = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
              onProgress?.({
                loadMessage: "Uploading instruction image...",
                progress: percent
              });
            }
          });

          console.log("🔍 Media server response for instruction image:", {
            imageType: typeof image,
            imageValue: image,
            hasId: !!image?._id,
            hasSrc: !!image?.src,
            imageStringified: JSON.stringify(image)
          });

          // Validate returned image object
          if (image && typeof image === "object" && image._id && image.src) {
            console.log("✅ Instruction image uploaded successfully:", {
              imageId: image._id,
              imageSrc: image.src,
              fullImageObject: image
            });
            newPage.data.instruction.image = {
              _image: image
            };
          } else {
            console.error("❌ Invalid image object from media server:", image);
          }
        } catch (uploadError) {
          console.error("Failed to upload instruction image:", uploadError);
        }
      } else if (editor.data.instruction?.image?._image) {
        // Keep existing instruction image if no new upload
        const existingImage = editor.data.instruction.image._image;
        // Only keep if it's a valid S3 URL (not a blob URL)
        if (
          typeof existingImage === "object" &&
          existingImage._id &&
          existingImage.src &&
          !existingImage.src.startsWith("blob:")
        ) {
          newPage.data.instruction.image = {
            _image: existingImage
          };
        }
      }

      // Report progress after images
      onProgress?.({ loadMessage: "Processing videos...", progress: 20 });

      // ================================================================

      // === 先統計所有要上傳的檔案總大小 ===
      let totalBytes = 0;
      let uploadedBytes = 0;

      // 統計影片大小
      editor.data.videos?.forEach((element, index) => {
        if (element && formProps[`video_${index}`]) {
          totalBytes += formProps[`video_${index}`].size;
        }
      });
      //=========UploadVideoWithChunks Start =============================
      let fileName = null;
      let uploadId = null;
      let parts = [];

      const initiateUpload = async (fileInfo) => {
        fileName = null;
        uploadId = null;
        parts = [];

        const response = await mediaServerAddress.post(
          "/initiate-video-upload",
          fileInfo,
          {
            headers: {
              "Content-Type": "application/json"
            }
          }
        );

        fileName = response.data.fileName;
        uploadId = response.data.uploadId;
      };

      const completeUpload = async (videoInfo) => {
        const response = await mediaServerAddress.post(
          "/complete-video-upload",
          {
            fileName: fileName,
            uploadId: uploadId,
            parts: parts, // 每個分片的 PartNumber 和 ETag
            ...videoInfo
          },
          {
            headers: {
              "Content-Type": "application/json"
            }
          }
        );

        // ✅ Validate response structure
        if (!response || !response.data) {
          console.error("❌ Invalid response from video upload server");
          throw new Error("Invalid response from video upload server");
        }

        const video = response.data.video;

        // ✅ Validate video object (warnings only, still return data)
        if (!video || typeof video !== "object") {
          console.warn("⚠️ Invalid video response:", response.data);
          console.warn("Attempting to use response anyway");
          // Still return it - let the caller handle it
          return video;
        }

        if (!video._id) {
          console.warn("⚠️ Video missing _id:", video);
          console.warn("Video may not be properly saved");
          // Still return it - let the caller handle it
        }

        return video;
      };

      // const MAX_RETRIES = 3; // 最大重試次數
      // const RETRY_DELAY = 2000; // 重試延遲時間（毫秒）

      const uploadChunk = async (
        url,
        chunk,
        chunkIndex,
        fileName,
        uploadId,
        onUploadProgress,
        retries = 0
      ) => {
        try {
          // ✅ Recreate FormData on each attempt to avoid stale blob references
          const formData = new FormData();
          formData.append("chunk", chunk);
          formData.append("chunkIndex", chunkIndex);
          formData.append("uploadId", uploadId);
          formData.append("fileName", fileName);

          // 🔍 Log chunk upload attempt
          if (retries > 0) {
            console.log(
              `📤 Uploading chunk ${chunkIndex} (retry ${retries}/3)`,
              {
                chunkSize: chunk.size,
                fileName,
                uploadId: uploadId.substring(0, 20)
              }
            );
          }

          return await mediaServerAddress.post(url, formData, {
            headers: { "Content-Type": "multipart/form-data" },
            onUploadProgress
          });
        } catch (error) {
          // ✅ Check for ERR_UPLOAD_FILE_CHANGED error
          if (
            error.message &&
            error.message.includes("ERR_UPLOAD_FILE_CHANGED")
          ) {
            console.error(
              "❌ ERR_UPLOAD_FILE_CHANGED detected at chunk",
              chunkIndex,
              "- File changed during upload:",
              error
            );
            // Still throw - this will be caught at the top level
            throw new Error(
              `ERR_UPLOAD_FILE_CHANGED: Video file changed during upload at chunk ${chunkIndex}. The file may have been modified or moved.`
            );
          }

          // Retry on network errors (429, 500, etc.)
          if (retries < 3) {
            console.warn(
              `⚠️ Retrying chunk ${chunkIndex} upload (attempt ${retries + 1}/3)`,
              {
                errorStatus: error.response?.status,
                errorMessage: error.message,
                errorCode: error.code
              }
            );
            await new Promise((resolve) => setTimeout(resolve, 2000));
            // ✅ Retry with same chunk data - FormData will be recreated
            return uploadChunk(
              url,
              chunk,
              chunkIndex,
              fileName,
              uploadId,
              onUploadProgress,
              retries + 1
            );
          } else {
            console.error(
              `❌ Chunk ${chunkIndex} failed after 3 retries:`,
              error.message
            );
            throw error;
          }
        }
      };

      const uploadFileInChunks = async (file, videoInfo) => {
        const CHUNK_SIZE = 10 * 1024 * 1024; // 10MB 每片大小

        if (!uploadId) {
          console.error("❌ Upload has not been initiated - no uploadId");
          throw new Error("Upload initialization failed - no uploadId");
        }

        // ✅ Validate file is still valid before starting chunk upload
        if (!file || !file.size || typeof file.slice !== "function") {
          console.error("❌ Video file is invalid:", {
            hasFile: !!file,
            hasSize: !!file?.size,
            hasSlice: typeof file?.slice === "function"
          });
          throw new Error(
            "Video file is no longer valid. Please re-select the file and try again."
          );
        }

        const totalChunks = Math.ceil(file.size / CHUNK_SIZE);

        for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
          const start = chunkIndex * CHUNK_SIZE;
          const end = Math.min(start + CHUNK_SIZE, file.size);

          // ✅ Validate file is still valid before each chunk
          if (!file || !file.size) {
            console.error(
              `❌ Video file became invalid at chunk ${chunkIndex}`
            );
            throw new Error(
              `Video file became invalid at chunk ${chunkIndex}. Upload aborted.`
            );
          }

          // ✅ Validate file.slice operation
          let chunk;
          try {
            chunk = file.slice(start, end); // 分片文件

            // ✅ Verify chunk was created successfully
            if (!chunk || chunk.size === 0) {
              console.error(`❌ Failed to create chunk ${chunkIndex}`);
              throw new Error(`Failed to create chunk ${chunkIndex}`);
            }
          } catch (error) {
            console.error(
              `❌ Failed to slice video file at chunk ${chunkIndex}:`,
              error
            );
            throw new Error(
              "Failed to read video file. The file may have been moved or modified. Please re-select and try again."
            );
          }

          const onUploadProgress = (e) => {
            const percent = ((uploadedBytes + e.loaded) / totalBytes) * 100;
            onProgress?.({
              loadMessage: `Uploading video...`,
              progress: Math.round(percent)
            });
          };

          // ✅ Pass chunk data directly instead of FormData
          // This allows uploadChunk to recreate FormData on each retry
          const response = await uploadChunk(
            "/upload-video-chunk",
            chunk,
            chunkIndex,
            fileName,
            uploadId,
            onUploadProgress
          );
          uploadedBytes += chunk.size; // 這個分片完成後，加入總已上傳
          parts.push({ ETag: response.data.ETag, PartNumber: chunkIndex + 1 });
        }
        return await completeUpload(videoInfo);
      };

      const videoMultiPartUpload = async (file, videoInfo) => {
        try {
          await initiateUpload(videoInfo);
          return await uploadFileInChunks(file, videoInfo);
        } catch (error) {
          console.error("Video multi-part upload failed:", error);
          throw error; // Re-throw to allow proper error handling
        }
      };

      //=========UploadVideoWithChunks End =============================

      newPage.data.videos =
        editor.data.videos &&
        (await Promise.all(
          // walk through the video fields in editor and load the value from form
          editor.data.videos.map(async (element, index) => {
            if (element === null) return null;
            const { _video } = element;

            // video still in use, remove form useless list
            if (_video?._id && uselessVideos[_video._id]) {
              delete uselessVideos[_video._id];
            }

            // if video is uploaded
            if (formProps[`video_${index}`]) {
              // if old video exist, delete old video, mark useless
              if (_video?._id) uselessVideos[_video._id] = true;

              const videoFile = formProps[`video_${index}`];

              // ✅ Validate file object before upload
              if (
                !videoFile ||
                !(videoFile instanceof File) ||
                !videoFile.size ||
                !videoFile.name
              ) {
                console.warn(
                  `⚠️ Invalid video file at index ${index}:`,
                  videoFile
                );
                console.warn(
                  "Skipping video upload - keeping existing video data"
                );
                return element; // Keep existing video data
              }

              const videoInfo = {
                size: videoFile.size,
                mimetype: videoFile.type,
                originalname: videoFile.name
              };

              try {
                // 🔍 Log video upload start
                console.log(`🎬 Starting video upload at index ${index}`, {
                  fileName: videoFile.name,
                  fileSize: videoFile.size,
                  fileType: videoFile.type,
                  timestamp: new Date().toISOString()
                });

                // 使用分段上傳並獲取上傳完成的影片資料
                const video = await videoMultiPartUpload(videoFile, videoInfo);

                // ✅ CRITICAL DEBUG: Check what we got back
                console.group("🎬 VIDEO UPLOAD COMPLETE");
                console.log("video:", video);
                console.log("video type:", typeof video);
                console.log(
                  "video is object:",
                  typeof video === "object" && video !== null
                );
                console.log("video is string:", typeof video === "string");
                console.log("video._id:", video?._id);
                console.log(
                  "video stringified:",
                  JSON.stringify(video)?.substring(0, 200)
                );

                // If video is undefined or null, log warning and keep existing
                if (!video) {
                  console.error("❌ Video upload returned null/undefined");
                  console.groupEnd();
                  console.warn(
                    "⚠️ Video upload failed - keeping existing video data"
                  );
                  return element; // Keep existing video
                }

                // If it's a string, try to parse it
                if (typeof video === "string") {
                  console.warn(
                    "⚠️ BACKEND RETURNED STRING! Attempting to parse..."
                  );
                  try {
                    const parsed = JSON.parse(video);
                    // Validate parsed object has _id
                    if (!parsed || !parsed._id) {
                      console.error("❌ Parsed video object missing _id");
                      console.groupEnd();
                      console.warn(
                        "⚠️ Failed to parse video - keeping existing video data"
                      );
                      return element;
                    }
                    console.log(
                      "✅ Successfully parsed video string to object:",
                      parsed
                    );
                    console.groupEnd();
                    return { ...element, _video: parsed };
                  } catch (e) {
                    console.error("❌ Failed to parse video string:", e);
                    console.groupEnd();
                    console.warn(
                      "⚠️ Failed to parse video - keeping existing video data"
                    );
                    return element;
                  }
                }

                // Validate it's a proper object with _id
                if (typeof video !== "object" || !video._id) {
                  console.error("❌ Invalid video object:", video);
                  console.groupEnd();
                  console.warn(
                    "⚠️ Invalid video object - keeping existing video data"
                  );
                  return element;
                }

                console.log("✅ Video object is valid");
                console.groupEnd();

                return { ...element, _video: video };
              } catch (error) {
                console.error("❌ Video upload failed:", error);
                console.warn(
                  "⚠️ Video upload error - keeping existing video data"
                );
                // Return original element to preserve existing data
                return element;
              }
            }

            return element;
          })
        ));

      // clean up deleted videos
      newPage.data.videos =
        newPage.data.videos && newPage.data.videos.filter((element) => element);
      // ===================================================================

      // Report progress after videos
      onProgress?.({ loadMessage: "Processing audios...", progress: 40 });

      newPage.data.audios =
        editor.data.audios &&
        (await Promise.all(
          // walk through the audio fields in editor and load the value from form
          editor.data.audios.map(async (element, index) => {
            if (element === null) return null;

            const { _audio } = element;

            // audio still in use, remove form useless list
            if (_audio?._id && uselessAudios[_audio._id])
              delete uselessAudios[_audio._id];

            // Check if audio needs to be uploaded (from editor state or formProps)
            const audioFile = _audio?.file || formProps[`audio_${index}`];

            // if audio is uploaded
            if (audioFile) {
              // if old audio exist, delete old audio, mark useless
              if (_audio?._id) uselessAudios[_audio._id] = true;

              const configs = {
                headers: {
                  [`content-type`]: `multipart/form-data`
                }
              };

              const fd = new FormData();

              fd.append("audio", audioFile);

              // upload new audio
              const {
                data: { audio }
              } = await axios.post(`audio`, fd, configs);

              return { ...element, _audio: audio };
            }

            // String _audio = plain ObjectId reference; preserve. Only
            // object-with-blob-src is the genuine "needs re-upload" case.
            if (typeof _audio === "string") return element;
            if (typeof _audio === "object" && _audio?.src?.startsWith("blob:")) {
              console.warn(
                "Warning: Detected blob URL in audio, needs re-upload:",
                _audio?.src
              );
              return null; // Don't save blob URLs
            }

            return element;
          })
        ));

      // clean up deleted audio
      newPage.data.audios =
        newPage.data.audios && newPage.data.audios.filter((element) => element);

      // Report progress after audios
      onProgress?.({ loadMessage: "Processing cards...", progress: 60 });

      newPage.data.cards =
        editor.data.cards &&
        (await Promise.all(
          // walk through the card fields in editor and load the value from form
          editor.data.cards.map(async (card, index) => {
            if (card === null) return null;

            const newCard = { ...card };

            // images still in use, remove from useless list
            if (card.images) {
              card.images.forEach((image) => {
                if (image?._image?._id && uselessImages[image._image._id])
                  delete uselessImages[image._image._id];
              });

              // walk through the images of the card
              for (
                let subIndex = 0;
                subIndex < card.images.length;
                subIndex++
              ) {
                const cardImage = card.images[subIndex];
                const cardImageFile =
                  cardImage?._image?.file ||
                  formProps[`cardImage_${index}_${subIndex}`];

                if (cardImageFile) {
                  // if old image exist, delete old image, mark useless
                  if (cardImage?._image?._id)
                    uselessImages[cardImage._image._id] = true;

                  const fd = new FormData();

                  fd.append("image", cardImageFile);

                  // upload new image
                  const {
                    data: { image }
                  } = await mediaServerAddress.post(`image`, fd);

                  newCard.images[subIndex] = {
                    ...card.images[subIndex],
                    _image: image
                  };
                }
              }
            }

            // image still in use, remove form useless list
            if (card.image?._image?._id && uselessImages[card.image._image._id])
              delete uselessImages[card.image._image._id];

            // Check if card single image needs to be uploaded
            const cardSingleImageFile =
              card.image?._image?.file || formProps[`cardImage_${index}`];

            // if image is uploaded
            if (cardSingleImageFile) {
              // if old image exist, delete old image, mark useless
              if (card.image?._image?._id)
                uselessImages[card.image._image._id] = true;

              const fd = new FormData();

              fd.append("image", cardSingleImageFile);

              // upload new image

              const {
                data: { image }
              } = await mediaServerAddress.post(`image`, fd, {
                onUploadProgress: (progressEvent) => {
                  const percent = Math.round(
                    (progressEvent.loaded * 100) / progressEvent.total
                  );

                  progressMap[index] = percent;

                  const values = Object.values(progressMap);
                  const avgProgress = Math.round(
                    values.reduce((a, b) => a + b, 0) / values.length
                  );
                  onProgress?.({
                    loadMessage: `Uploading card single image...`,
                    progress: avgProgress
                  });
                }
              });
              newCard.image = { ...card.image, _image: image };
            }

            if (card.audios) {
              // audios still in use, remove from useless list
              card.audios.forEach((audio) => {
                if (audio?._audio?._id && uselessAudios[audio._audio._id])
                  delete uselessAudios[audio._audio._id];
              });
            }

            // walk through the audios of the card
            if (card.audios) {
              for (
                let subIndex = 0;
                subIndex < card.audios.length;
                subIndex++
              ) {
                const cardAudio = card.audios[subIndex];
                const cardAudioFile =
                  cardAudio?._audio?.file ||
                  formProps[`cardAudio_${index}_${subIndex}`];

                if (cardAudioFile) {
                  // if old audio exist, delete old audio, mark useless
                  if (cardAudio?._audio?._id)
                    uselessAudios[cardAudio._audio._id] = true;

                  const configs = {
                    headers: {
                      [`content-type`]: `multipart/formdata`,
                      apptype: "ta"
                    }
                  };

                  const fd = new FormData();

                  fd.append("audio", cardAudioFile);

                  // upload new audio
                  const {
                    data: { audio }
                  } = await axios.post(`audio`, fd, configs);

                  newCard.audios[subIndex] = {
                    ...card.audios[subIndex],
                    _audio: audio
                  };
                }
              }
            }

            // Clean up blob URLs in card audios. String _audio = plain
            // ObjectId reference (PDF bulk-create / non-populated fetch);
            // preserve. Drop only object-with-blob-src.
            if (newCard.audios) {
              newCard.audios = newCard.audios.map((audio) => {
                if (!audio) return audio;
                const { _audio } = audio;
                if (typeof _audio === "string") return audio;
                if (typeof _audio === "object" && _audio?.src?.startsWith("blob:")) {
                  console.warn(
                    "Warning: Detected blob URL in card audio, needs re-upload:",
                    _audio?.src
                  );
                  return null;
                }
                return audio;
              });
            }

            // audio still in use, remove form useless list
            if (card.audio?._audio?._id && uselessAudios[card.audio._audio._id])
              delete uselessAudios[card.audio._audio._id];

            // Check if card single audio needs to be uploaded
            const cardSingleAudioFile =
              card.audio?._audio?.file || formProps[`cardAudio_${index}`];

            // if audio is uploaded
            if (cardSingleAudioFile) {
              // if old audio exist, delete old audio, mark useless
              if (card.audio?._audio?._id)
                uselessAudios[card.audio._audio._id] = true;

              const configs = {
                headers: {
                  [`content-type`]: `multipart/form-data`
                }
              };

              const fd = new FormData();

              fd.append("audio", cardSingleAudioFile);

              // upload new audio
              const {
                data: { audio }
              } = await axios.post(`audio`, fd, configs);

              newCard.audio = { ...card.audio, _audio: audio };
            }

            // Clean up blob URL in card single audio. String _audio is a
            // plain ObjectId reference; keep. Drop only blob: src.
            if (newCard.audio) {
              const { _audio } = newCard.audio;
              if (typeof _audio === "object" && _audio?.src?.startsWith("blob:")) {
                console.warn(
                  "Warning: Detected blob URL in card audio, needs re-upload:",
                  _audio?.src
                );
                newCard.audio = null;
              }
            }

            if (card.texts) {
              card.texts.forEach((text, subIndex) => {
                if (text) {
                  newCard.texts[subIndex] = {
                    ...text,
                    value: formProps[`cardText_${index}_${subIndex}`]
                  };
                }
              });
            }

            // card remarks
            if (card.remarks) {
              card.remarks.forEach((remark, subIndex) => {
                if (remark) {
                  newCard.remarks[subIndex] = {
                    ...remark,
                    value: formProps[`cardRemark_${index}_${subIndex}`]
                  };
                }
              });
            }

            if (card.text) {
              newCard.text = {
                ...card.text,
                value: formProps[`cardText_${index}`]
              };
            }

            // card remark
            if (card.remark) {
              newCard.remark = {
                ...card.remark,
                value: formProps[`cardRemark_${index}`]
              };
            }

            newCard.title = formProps[`cardTitle_${index}`];

            return newCard;
          })
        ));

      // clean up deleted cards
      newPage.data.cards =
        newPage.data.cards &&
        newPage.data.cards.filter((element) => {
          return element !== null;
        });

      // Report progress before cleanup
      onProgress?.({ loadMessage: "Cleaning up old files...", progress: 80 });

      // ── Defence in depth: never delete a media ID that's still actually
      // referenced in the page we're about to save. The per-field upsert
      // loops above should already have pruned uselessImages/Audios/Videos,
      // but if anything slipped through (e.g. PDF-created pages where
      // `_image` arrived as a plain ObjectId string and the bookkeeping
      // skipped it, then ADD_AUDIO triggered a save that left the cleanup
      // list stale) the image S3 file would be hard-deleted and the page
      // would re-render with a 404. Re-scan newPage and remove any in-use
      // IDs from the kill lists before the DELETE calls fire. ──
      const collectImageIds = (out, arr) => {
        if (!Array.isArray(arr)) return;
        for (const el of arr) {
          if (!el) continue;
          const img = el._image;
          if (!img) continue;
          if (typeof img === "string") out.add(img);
          else if (typeof img === "object" && img._id) out.add(String(img._id));
        }
      };
      const collectAudioIds = (out, arr) => {
        if (!Array.isArray(arr)) return;
        for (const el of arr) {
          if (!el) continue;
          const aud = el._audio;
          if (!aud) continue;
          if (typeof aud === "string") out.add(aud);
          else if (typeof aud === "object" && aud._id) out.add(String(aud._id));
        }
      };
      const collectVideoIds = (out, arr) => {
        if (!Array.isArray(arr)) return;
        for (const el of arr) {
          if (!el) continue;
          const v = el._video;
          if (!v) continue;
          if (typeof v === "string") out.add(v);
          else if (typeof v === "object" && v._id) out.add(String(v._id));
        }
      };
      const stillUsedImageIds = new Set();
      const stillUsedAudioIds = new Set();
      const stillUsedVideoIds = new Set();
      collectImageIds(stillUsedImageIds, newPage.data.images);
      collectImageIds(stillUsedImageIds, newPage.data.contentImages);
      collectAudioIds(stillUsedAudioIds, newPage.data.audios);
      collectVideoIds(stillUsedVideoIds, newPage.data.videos);
      if (Array.isArray(newPage.data.cards)) {
        for (const c of newPage.data.cards) {
          if (!c) continue;
          // single image / audio
          if (c.image?._image) {
            const img = c.image._image;
            if (typeof img === "string") stillUsedImageIds.add(img);
            else if (img._id) stillUsedImageIds.add(String(img._id));
          }
          if (c.audio?._audio) {
            const aud = c.audio._audio;
            if (typeof aud === "string") stillUsedAudioIds.add(aud);
            else if (aud._id) stillUsedAudioIds.add(String(aud._id));
          }
          collectImageIds(stillUsedImageIds, c.images);
          collectAudioIds(stillUsedAudioIds, c.audios);
        }
      }
      // instruction image
      if (newPage.data.instruction?.image?._image) {
        const img = newPage.data.instruction.image._image;
        if (typeof img === "string") stillUsedImageIds.add(img);
        else if (img._id) stillUsedImageIds.add(String(img._id));
      }
      // _thumbnail
      if (newPage._thumbnail) {
        const t = newPage._thumbnail;
        if (typeof t === "string") stillUsedImageIds.add(t);
        else if (t._id) stillUsedImageIds.add(String(t._id));
      }
      for (const id of stillUsedImageIds) delete uselessImages[id];
      for (const id of stillUsedAudioIds) delete uselessAudios[id];
      for (const id of stillUsedVideoIds) delete uselessVideos[id];

      // Second protection layer: if the image is in `newPage.data.images`
      // but its IMAGE PROCESSING PATH dropped it (returned null) for ANY
      // reason — including being a {_id, but no src} populated stub or any
      // future edge case we miss — we'd have an inconsistency: newPage
      // strips the image, kill list keeps its ID, image gone forever.
      //
      // So we also collect every media ID referenced in the ORIGINAL page
      // (the one we're editing). The only IDs left in uselessImages after
      // this pass are ones the USER explicitly replaced via fresh upload
      // (tracked via uselessImages[X] = true in the upload branch BEFORE
      // the in-use check). That's the only legitimate hard-delete case.
      const originalPage = pageMap[editor._id];
      const originalUsedImageIds = new Set();
      const originalUsedAudioIds = new Set();
      const originalUsedVideoIds = new Set();
      if (originalPage?.data) {
        collectImageIds(originalUsedImageIds, originalPage.data.images);
        collectImageIds(originalUsedImageIds, originalPage.data.contentImages);
        collectAudioIds(originalUsedAudioIds, originalPage.data.audios);
        collectVideoIds(originalUsedVideoIds, originalPage.data.videos);
        if (Array.isArray(originalPage.data.cards)) {
          for (const c of originalPage.data.cards) {
            if (!c) continue;
            if (c.image?._image) {
              const img = c.image._image;
              if (typeof img === "string") originalUsedImageIds.add(img);
              else if (img._id) originalUsedImageIds.add(String(img._id));
            }
            if (c.audio?._audio) {
              const aud = c.audio._audio;
              if (typeof aud === "string") originalUsedAudioIds.add(aud);
              else if (aud._id) originalUsedAudioIds.add(String(aud._id));
            }
            collectImageIds(originalUsedImageIds, c.images);
            collectAudioIds(originalUsedAudioIds, c.audios);
          }
        }
        if (originalPage.data.instruction?.image?._image) {
          const img = originalPage.data.instruction.image._image;
          if (typeof img === "string") originalUsedImageIds.add(img);
          else if (img._id) originalUsedImageIds.add(String(img._id));
        }
      }
      if (originalPage?._thumbnail) {
        const t = originalPage._thumbnail;
        if (typeof t === "string") originalUsedImageIds.add(t);
        else if (t._id) originalUsedImageIds.add(String(t._id));
      }
      // Anything in the original page that is ALSO still in newPage stays
      // alive. An ID present in the original kill list (uselessImages[id])
      // is only legitimately killed if BOTH:
      //   - it's in original (so its ID landed in uselessImages at init), AND
      //   - it was explicitly marked useless during processing (upload branch
      //     bumped it back into uselessImages)
      // The processing upload branch was: `uselessImages[_image._id] = true`
      // — we can't distinguish init-only vs upload-marked from the object alone.
      // Safe stance: if an ID is in the original AND in newPage, never delete.
      for (const id of originalUsedImageIds) {
        if (stillUsedImageIds.has(id)) delete uselessImages[id];
      }
      for (const id of originalUsedAudioIds) {
        if (stillUsedAudioIds.has(id)) delete uselessAudios[id];
      }
      for (const id of originalUsedVideoIds) {
        if (stillUsedVideoIds.has(id)) delete uselessVideos[id];
      }

      // Audit log — surfaces any unexpected kill so the user can spot
      // regressions in dev tools without ploughing through the action.
      if (Object.keys(uselessImages).length > 0 || Object.keys(uselessAudios).length > 0) {
        console.info("[savePage] media cleanup", {
          pageId: editor._id,
          deletingImageIds: Object.keys(uselessImages),
          deletingAudioIds: Object.keys(uselessAudios),
          deletingVideoIds: Object.keys(uselessVideos),
          keptImageIds: [...stillUsedImageIds],
          keptAudioIds: [...stillUsedAudioIds]
        });
      }

      // clean up old images
      await axios.delete(`image`, {
        data: { imageIds: Object.keys(uselessImages) }
      });

      // clean up old video
      await axios.delete(`video`, {
        data: { videoIds: Object.keys(uselessVideos) }
      });

      // clean up old audios
      await axios.delete(`audio`, {
        data: { audioIds: Object.keys(uselessAudios) }
      });

      // Report progress before backend save
      onProgress?.({ loadMessage: "Saving to server...", progress: 90 });

      // Log contentTexts
      if (newPage.data?.contentTexts) {
        newPage.data.contentTexts.forEach((ct, idx) => {
          console.log(`contentTexts[${idx}]:`, {
            exist: ct?.exist,
            hasValue: !!ct?.value,
            valuePreview: ct?.value
              ? ct.value.substring(0, 150) + "..."
              : "null",
            fullLength: ct?.value?.length || 0
          });
        });
      } else {
        console.log("contentTexts: NOT PRESENT");
      }

      // Log contentRemarks (STYLED TEXT from dialog) - THIS IS THE KEY FIELD FOR STYLED TEXT
      if (newPage.data?.contentRemarks) {
        newPage.data.contentRemarks.forEach((cr, idx) => {
          const hasStyledText =
            cr?.value && cr.value.includes("inlineStyleRanges");
        });
      } else {
        console.error(
          "❌ contentRemarks: NOT PRESENT - Styled text will NOT be saved!"
        );
      }

      console.groupEnd();

      // 🔍 SEONGGGG - Before sending to backend
      console.log("🔍 SEONGGGG - Before axios.patch to backend", {
        pageId: editor._id,
        timestamp: new Date().toISOString(),
        instructionData: newPage.data?.instruction,
        instructionImageType: typeof newPage.data?.instruction?.image?._image,
        instructionImageValue: newPage.data?.instruction?.image?._image
      });

      // 🔍 SEONGGGG - Deep inspection of videos/images before sending
      if (newPage.data?.videos) {
        newPage.data.videos.forEach((video, index) => {
          if (video) {
            // console.log(`🔍 SEONGGGG - Video[${index}] structure:`, {
            //   type: typeof video,
            //   isString: typeof video === "string",
            //   hasVideoProperty: video._video !== undefined,
            //   videoPropertyType: video._video
            //     ? typeof video._video
            //     : "undefined",
            //   preview:
            //     typeof video === "string"
            //       ? video.substring(0, 300)
            //       : JSON.stringify(video).substring(0, 300)
            // });
            // if (typeof video === "string") {
            //   console.error(
            //     `🔍 SEONGGGG - CRITICAL: video[${index}] is STRING before backend!`,
            //     {
            //       value: video.substring(0, 500)
            //     }
            //   );
            // } else if (video._video && typeof video._video === "string") {
            //   console.error(
            //     `🔍 SEONGGGG - CRITICAL: video[${index}]._video is STRING!`,
            //     {
            //       value: video._video.substring(0, 500)
            //     }
            //   );
            // }
          }
        });
      }

      if (newPage.data?.images) {
        newPage.data.images.forEach((image, index) => {
          if (image) {
            // console.log(`🔍 SEONGGGG - Image[${index}] structure:`, {
            //   type: typeof image,
            //   isString: typeof image === "string",
            //   hasImageProperty: image._image !== undefined,
            //   imagePropertyType: image._image
            //     ? typeof image._image
            //     : "undefined",
            //   preview:
            //     typeof image === "string"
            //       ? image.substring(0, 300)
            //       : JSON.stringify(image).substring(0, 300)
            // });
            // if (typeof image === "string") {
            //   console.error(
            //     `🔍 SEONGGGG - CRITICAL: image[${index}] is STRING before backend!`,
            //     {
            //       value: image.substring(0, 500)
            //     }
            //   );
            // } else if (image._image && typeof image._image === "string") {
            //   console.error(
            //     `🔍 SEONGGGG - CRITICAL: image[${index}]._image is STRING!`,
            //     {
            //       value: image._image.substring(0, 500)
            //     }
            //   );
            // }
          }
        });
      }

      const {
        data: { page }
      } = await axios.patch(`page/${editor._id}`, {
        patch: {
          action: `UPDATE`,
          data: newPage,
          existFileSrcs,
          existImageSrcs
        }
      });

      // 🔍 SEONGGGG - Backend response received
      console.log("🔍 SEONGGGG - Backend response SUCCESS", {
        pageId: editor._id,
        timestamp: new Date().toISOString(),
        responseHasPage: !!page,
        responseHasData: !!page?.data,
        responseInstruction: page?.data?.instruction,
        responseInstructionImageType:
          typeof page?.data?.instruction?.image?._image,
        responseInstructionImageValue: page?.data?.instruction?.image?._image
      });

      // 🔍 Check if backend saved contentRemarks
      console.group("🔍 BACKEND RESPONSE - contentRemarks verification");

      if (page?.data?.contentRemarks) {
        page.data.contentRemarks.forEach((cr, idx) => {});
      } else {
        console.error(
          "❌ Backend did NOT return contentRemarks - backend may not be saving it!"
        );
      }
      console.groupEnd();

      const updatedPage = {
        ...pageMap[editor._id],
        ...newPage
      };

      updatedPage.data._files = page.data._files; // update download page._files

      dispatch({ type: UPDATE_PAGE, payload: { page: updatedPage } });

      // Report completion
      onProgress?.({ loadMessage: "Save complete!", progress: 100 });

      if (callback) callback();
    } catch (error) {
      // 🔍 SEONGGGG - Save page error handler
      // console.error("🔍 SEONGGGG - savePage ERROR CAUGHT", {
      //   pageId: editor?._id,
      //   timestamp: new Date().toISOString(),
      //   errorMessage: error.message,
      //   errorName: error.name,
      //   errorCode: error.code,
      //   responseStatus: error.response?.status,
      //   responseStatusText: error.response?.statusText,
      //   responseData: error.response?.data,
      //   responseHeaders: error.response?.headers,
      //   requestURL: error.config?.url,
      //   requestMethod: error.config?.method,
      //   isNetworkError: !error.response && error.request,
      //   networkErrorDetails:
      //     !error.response && error.request
      //       ? {
      //           readyState: error.request.readyState,
      //           status: error.request.status,
      //           statusText: error.request.statusText
      //         }
      //       : null,
      //   // Check if data was corrupted before sending
      //   sentDataSnapshot: {
      //     hasVideos: newPage?.data?.videos !== undefined,
      //     hasImages: newPage?.data?.images !== undefined,
      //     videosType: newPage?.data?.videos
      //       ? typeof newPage.data.videos
      //       : "undefined",
      //     imagesType: newPage?.data?.images
      //       ? typeof newPage.data.images
      //       : "undefined"
      //   },
      //   stackTrace: error.stack
      // });

      console.error("Error saving page:", error);

      // Show error to user
      dispatch({
        type: "ERROR",
        payload: {
          message: `Failed to save page: ${
            error.message || "Unknown error"
          }. Please check the console for details.`
        }
      });

      // Report to Rollbar if available
      if (window.Rollbar) {
        window.Rollbar.error("Error saving page", error, {
          formProps,
          editorId: getStore().page.editor?._id,
          editorStyle: getStore().page.editor?.style,
          editorStyleType: getStore().page.editor?.styleType
        });
      }

      // Reset progress indicator
      if (options.onProgress) {
        options.onProgress({ loadMessage: "Save failed", progress: 0 });
      }

      throw error; // Re-throw to allow caller to handle if needed
    }
  };

// remove the page
export const removePage =
  ({ pageType, pageId, sectionId }, callback) =>
  async (dispatch, getStore) => {
    const pageMap = getStore().page.pageMap;

    let res;
    let pageRes;
    switch (pageType) {
      case `teachingAssistant`:
        res = await axios.patch(`section/${sectionId}`, {
          patch: {
            action: `REMOVE_TEACHING_ASSISTANT`,
            data: { pageId }
          }
        });

        if (pageMap[pageId] && pageMap[pageId]._section === sectionId) {
          pageRes = await axios.patch(`page/${pageId}`, {
            patch: { action: `REMOVE` }
          });

          batch(() => {
            dispatch({
              type: UPDATE_SECTION,
              payload: { section: res.data?.section }
            });

            dispatch({
              type: UPDATE_PAGE,
              payload: { page: pageRes.data.page }
            });
          });
        } else {
          dispatch({
            type: UPDATE_SECTION,
            payload: { section: res.data?.section }
          });
        }

        break;

      default:
        return dispatch({
          type: SET_ERROR,
          payload: `Unprocessable Page Type`
        });
    }

    if (callback) callback();
  };

// restore the page
export const restorePage =
  ({ pageType, pageId, sectionId }, callback) =>
  async (dispatch, getStore) => {
    const {
      data: { page }
    } = await axios.patch(`page/${pageId}`, {
      patch: { action: `RESTORE` }
    });

    let res;
    switch (pageType) {
      case `teachingAssistant`:
        res = await axios.patch(`section/${sectionId}`, {
          patch: {
            action: `RESTORE_TEACHING_ASSISTANT`,
            data: { pageId }
          }
        });

        batch(() => {
          dispatch({
            type: UPDATE_SECTION,
            payload: { section: res.data?.section }
          });

          dispatch({ type: UPDATE_PAGE, payload: { page } });
        });
        break;

      default:
        return dispatch({
          type: SET_ERROR,
          payload: `Unprocessable Page Type`
        });
    }

    if (callback) callback();
  };

export const restorePages =
  ({ pageType, pageIds, sectionId }, callback) =>
  async (dispatch, getStore) => {
    await axios.patch(`page`, {
      patch: { action: `RESTORE` },
      pageIds
    });

    let res;
    switch (pageType) {
      case `teachingAssistant`:
        res = await axios.patch(`section/${sectionId}`, {
          patch: {
            action: `RESTORE_TEACHING_ASSISTANT`,
            data: { pageIds }
          }
        });

        batch(() => {
          dispatch({
            type: UPDATE_SECTION,
            payload: { section: res.data?.section }
          });

          dispatch({
            type: RESTORE_PAGES,
            payload: { pageIds }
          });
        });
        break;

      default:
        return dispatch({
          type: SET_ERROR,
          payload: `Unprocessable Page Type`
        });
    }

    if (callback) callback();
  };

// delete the page
export const deletePage =
  (
    {
      pageType,
      pageId,
      sectionId,
      unitId,
      teachingVideoId,
      resourceId,
      trainingId
    },
    callback
  ) =>
  async (dispatch, getStore) => {
    let res;
    switch (pageType) {
      case `teachingAssistant`:
        res = await axios.patch(`section/${sectionId}`, {
          patch: {
            action: `DELETE_TEACHING_ASSISTANT`,
            data: { pageId }
          }
        });

        dispatch({
          type: UPDATE_SECTION,
          payload: { section: res.data?.section }
        });
        break;

      case 1:
      case `lessonPlan`:
        res = await axios.patch(`unit/${unitId}`, {
          patch: {
            action: `DELETE_LESSON_PLAN`,
            data: { pageId }
          }
        });

        dispatch({
          type: UPDATE_UNIT,
          payload: { unit: res.data?.unit }
        });
        break;

      case 2:
      case `teachingVideo`:
        res = await axios.patch(`unit/${unitId}`, {
          patch: {
            action: `DELETE_TEACHING_VIDEO`,
            data: { pageId, teachingVideoId }
          }
        });

        dispatch({
          type: UPDATE_UNIT,
          payload: { unit: res.data?.unit }
        });

        break;

      case `resource`:
        res = await axios.patch(`resource/${resourceId}`, {
          patch: {
            action: `DELETE_PAGE`,
            data: { pageId }
          }
        });

        dispatch({
          type: UPDATE_RESOURCE,
          payload: { resource: res.data?.resource }
        });

        break;

      case `trainingVideo`:
        res = await axios.patch(`training/${trainingId}`, {
          patch: {
            action: `DELETE_TRAINING_VIDEO`,
            data: { pageId }
          }
        });

        dispatch({
          type: UPDATE_TRAINING,
          payload: { training: res.data?.training }
        });

        break;

      default:
        return dispatch({
          type: SET_ERROR,
          payload: `Unprocessable Page Type`
        });
    }

    if (callback) callback();
  };

// delete the page
export const deletePages =
  ({ pageType, pageIds, sectionId }, callback) =>
  async (dispatch, getStore) => {
    let res;
    switch (pageType) {
      case `teachingAssistant`:
        res = await axios.patch(`section/${sectionId}`, {
          patch: {
            action: `DELETE_TEACHING_ASSISTANT`,
            data: { pageIds }
          }
        });

        dispatch({
          type: UPDATE_SECTION,
          payload: { section: res.data?.section }
        });
        break;

      default:
        return dispatch({
          type: SET_ERROR,
          payload: `Unprocessable Page Type`
        });
    }

    callback();
  };

// move the page
export const movePage =
  ({ pageType, pageId, sectionId }, to, callback) =>
  async (dispatch, getStore) => {
    let res;
    switch (pageType) {
      case `teachingAssistant`:
        res = await axios.patch(`section/${sectionId}`, {
          patch: {
            action: `MOVE_TEACHING_ASSISTANT`,
            data: { pageId, to }
          }
        });

        dispatch({
          type: UPDATE_SECTION,
          payload: { section: res.data?.section }
        });
        break;

      default:
        return dispatch({
          type: SET_ERROR,
          payload: `Unprocessable Page Type`
        });
    }

    if (callback) callback();
  };

// change the title
export const changeTitle = (value) => ({
  type: CHANGE_TITLE,
  payload: { value }
});

// change the teaching notes
export const changeTeachingNotes = (value) => ({
  type: CHANGE_TEACHING_NOTES,
  payload: { value }
});

// change the remark teaching notes
export const changeRemarkTeachingNotes = (value) => ({
  type: CHANGE_REMARK_TEACHING_NOTES,
  payload: { value }
});

// change the text
export const changeText = (index, value) => ({
  type: CHANGE_TEXT,
  payload: { index, value }
});

// change the remark
export const changeRemarkText = (index, value) => ({
  type: CHANGE_REMARK_TEXT,
  payload: { index, value }
});

// Reading Style 10 audio-highlight on/off toggle (admin opts in/out per page).
export const setAudioHighlightEnabled = (enabled) => ({
  type: CHANGE_AUDIO_HIGHLIGHT_ENABLED,
  payload: !!enabled
});

// change the content text (for QuizStyle15 multiple content feature)
export const changeContentText = (index, value) => ({
  type: CHANGE_CONTENT_TEXT,
  payload: { index, value }
});

// change the remark content text (for QuizStyle15 multiple content feature)
export const changeRemarkContentText = (index, value) => ({
  type: CHANGE_REMARK_CONTENT_TEXT,
  payload: { index, value }
});

// change the card text
export const changeCardText = (index, value, subIndex) => ({
  type: CHANGE_CARD_TEXT,
  payload: { index, value, subIndex }
});

// change the remark card text
export const changeRemarkCardText = (index, value, subIndex) => ({
  type: CHANGE_CARD_REMARK_TEXT,
  payload: { index, value, subIndex }
});

/**
 * IMAGE
 */

// change the image src
export const changeImage = (index, name, src, subIndex, file, fieldType) => ({
  type: CHANGE_IMAGE,
  payload: { index, name, src, subIndex, file, fieldType }
});

// remove the image src
export const removeImage = (index, fieldType) => ({
  type: REMOVE_IMAGE,
  payload: { index, fieldType }
});

/**
 * INSTRUCTION
 */

// change the instruction text
export const changeInstructionText = (text) => ({
  type: CHANGE_INSTRUCTION_TEXT,
  payload: { text }
});

// change the instruction image
export const changeInstructionImage = (index, name, src, subIndex, file) => ({
  type: CHANGE_INSTRUCTION_IMAGE,
  payload: { name, src, file }
});

// remove the instruction image
export const removeInstructionImage = () => ({
  type: REMOVE_INSTRUCTION_IMAGE
});

/**
 * VIDEO
 */

// change the Video src
export const changeVideo = (index, name, src) => ({
  type: CHANGE_VIDEO,
  payload: { index, name, src }
});

// remove the video src
export const removeVideo = (index) => ({
  type: REMOVE_VIDEO,
  payload: { index }
});

//change the video status
export const changeVideoStatus = (index, pageId, video) => ({
  type: CHANGE_VIDEO_STATUS,
  payload: { index, pageId, video }
});

/**
 * AUDIO
 */

// change the audio src
export const changeAudio = (index, name, src, subIndex, file) => ({
  type: CHANGE_AUDIO,
  payload: { index, name, src, subIndex, file }
});

// remove the audio src
export const removeAudio = (index) => ({
  type: REMOVE_AUDIO,
  payload: { index }
});

/**
 * AUDIO FOR TEMPLATE STYLE 1.1 ONLY
 */

// move audio
export const moveAudio = (index, position) => {
  return {
    type: MOVE_AUDIO,
    payload: { index, position }
  };
};

// add audio
export const addAudio = (audio) => {
  return { type: ADD_AUDIO, payload: { audio } };
};

// delete audio
export const deleteAudio = (index) => {
  return { type: DELETE_AUDIO, payload: { index } };
};

/**
 * CARD
 */

// change the image src
export const changeCardImage = (index, name, src, subIndex) => {
  return {
    type: CHANGE_CARD_IMAGE,
    payload: { index, name, src, subIndex }
  };
};

export const removeCardImage = (index, subIndex) => {
  return {
    type: REMOVE_CARD_IMAGE,
    payload: { index, subIndex }
  };
};

//change the audio src
export const changeCardAudio = (index, name, src, subIndex) => {
  return {
    type: CHANGE_CARD_AUDIO,
    payload: { index, subIndex, name, src }
  };
};

export const removeCardAudio = (index, subIndex) => {
  return {
    type: REMOVE_CARD_AUDIO,
    payload: { index, subIndex }
  };
};

// add card
export const addCard = (card) => {
  return { type: ADD_CARD, payload: { card } };
};

// delete card
export const deleteCard = (index) => {
  return { type: DELETE_CARD, payload: { index } };
};

//move card
export const moveCard = (index, position) => {
  return {
    type: MOVE_CARD,
    payload: { index, position }
  };
};

/**
 * PAGE
 */

export const openEditor = (pageId) => ({
  type: OPEN_EDITOR,
  payload: { pageId }
});

export const closeEditor = () => ({ type: CLOSE_EDITOR, payload: {} });

export const addTeachingVideoList =
  (unitId, callback) => async (dispatch, getStore) => {
    const {
      data: { unit }
    } = await axios.patch(`unit/${unitId}`, {
      patch: { action: `ADD_TEACHING_VIDEO_LIST` }
    });

    dispatch({
      type: UPDATE_UNIT,
      payload: { unit }
    });

    if (callback) callback();
  };

export const deleteTeachingVideoList =
  (unitId, teachingVideoId, callback) => async (dispatch, getStore) => {
    const {
      data: { unit }
    } = await axios.patch(`unit/${unitId}`, {
      patch: {
        action: `DELETE_TEACHING_VIDEO_LIST`,
        data: { teachingVideoId }
      }
    });

    dispatch({
      type: UPDATE_UNIT,
      payload: { unit }
    });

    if (callback) callback();
  };

// upload image (for download page)
export const uploadImage =
  (pageId, file, callback, onProgress) => async (dispatch, getStore) => {
    const fd = new FormData();

    fd.append("image", file);

    // upload new image

    const {
      data: { image }
    } = await mediaServerAddress.post(`image`, fd, {
      onUploadProgress: (progressEvent) => {
        if (onProgress) {
          const percent = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          onProgress(percent);
        }
      }
    });

    const {
      data: { page }
    } = await axios.patch(`page/${pageId}`, {
      patch: { action: `UPLOAD_IMAGE`, data: { imageId: image._id } }
    });

    dispatch({
      type: ADD_IMAGE,
      payload: { image: { _image: image } }
    });

    if (callback) callback(image.src);
  };

// ===================================================================

// upload video
export const uploadVideo =
  (pageId, file, callback) => async (dispatch, getStore) => {
    const fd = new FormData();

    fd.append("video", file);

    // upload new video

    const {
      data: { video }
    } = await mediaServerAddress.post(`video`, fd);

    const {
      data: { page }
    } = await axios.patch(`page/${pageId}`, {
      patch: { action: `UPLOAD_VIDEO`, data: { videoId: video._id } }
    });

    dispatch({
      type: UPDATE_PAGE,
      payload: { page }
    });

    dispatch({
      type: ADD_VIDEO,
      payload: { video: video }
    });

    if (callback) callback(video.src);
  };

// ===================================================================

// upload file
export const uploadFile =
  (pageId, newfile, callback, onProgress) => async (dispatch, getStore) => {
    const fd = new FormData();

    fd.append("file", newfile);

    // upload new file
    const {
      data: { file }
    } = await axios.post(`file`, fd, {
      headers: {
        [`content-type`]: `multipart/form-data`
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress) {
          const percent = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          onProgress(percent);
        }
      }
    });

    const {
      data: { page }
    } = await axios.patch(`page/${pageId}`, {
      patch: { action: `UPLOAD_FILE`, data: { fileId: file._id } }
    });

    dispatch({
      type: ADD_FILE,
      payload: { file: file._id }
    });

    if (callback) callback(file.src, file.name);
  };

export const uploadHTML5Zip = async ({ file, userId, change, dispatch }) => {
  dispatch({ type: SET_LOAD, payload: "Progress: init..." });
  const filename = file.name;
  const filetype = file.type;
  const baseURL = process.env.REACT_APP_HTML5EXPORT;
  const userURL = baseURL + userId + "/";
  const signurl = await axios.post(`signurl`, {
    destination: baseURL.match(/\w*\/$/i)[0] + userId + "/" + filename,
    filetype: filetype
  });
  dispatch({ type: SET_LOAD, payload: "Progress: uploading..." });
  const folderName = filename.replace(/.zip$/, "");
  const defaultPattern = userURL + folderName + "/index.html";
  const FolderNamePattern = userURL + folderName + "/" + folderName + ".html";
  const res = await axios.put(signurl.data, file, {
    headers: {
      "Content-Type": filetype
    }
  });

  dispatch({ type: SET_LOAD, payload: "Progress: unzipping..." });
  const validator = axios.create();
  let isResolve = false;
  const maxRetry = 25;
  const toPercent = 100 / maxRetry;
  validator.defaults.raxConfig = {
    instance: validator,
    retry: maxRetry,
    noResponseRetries: 5,
    retryDelay: 10000,
    statusCodesToRetry: [
      [100, 199],
      [400, 429],
      [500, 599]
    ],
    backoffType: "linear",
    shouldRetry: (err) => {
      if (isResolve) {
        return false;
      }
      return rax.shouldRetryRequest(err); // built-in logic
    },
    onRetryAttempt: (err) => {
      const cfg = rax.getConfig(err);

      dispatch({
        type: SET_LOAD,
        payload: `Progress: unzipping... (${
          cfg.currentRetryAttempt * toPercent
        } / 100)`
      });
    }
  };
  const interceptorId = rax.attach(validator);
  const promises = [
    validator.get(defaultPattern),
    validator.get(FolderNamePattern)
  ];

  let error = false;
  await Promise.any(promises)
    .then((values) => {
      isResolve = true;
      let htmlURL = values.config.url;
      change("text_0", htmlURL); // for save
      dispatch({
        type: CHANGE_TEXT,
        payload: { index: 0, value: htmlURL }
      }); // for display
    })
    .catch((err) => {
      rollbar.error(err);
      error = true;
    });

  dispatch({ type: CLOSE_LOAD });
  rax.detach(interceptorId);
  return error;
};

// upload thumbnail
export const uploadThumbnail =
  (pageId, thumbnail, callback) => async (dispatch, getStore) => {
    const fd = new FormData();

    fd.append("image", thumbnail, "thumbnail.jpg");

    // upload thumbnail
    const {
      data: { image }
    } = await mediaServerAddress.post(`image`, fd);

    const {
      data: { page }
    } = await axios.patch(`page/${pageId}`, {
      patch: { action: `UPLOAD_THUMBNAIL`, data: { imageId: image._id } }
    });

    dispatch({
      type: UPDATE_PAGE,
      payload: { page }
    });

    if (callback) {
      callback();
    }
  };

// fetch number of all the visible pages
export const fetchPagesNumber =
  (options, callback) => async (dispatch, getStore) => {
    const configs = {
      headers: { ClientType: "TA" }
    };

    let query = ``;

    if (options.pageType !== undefined)
      query = `${query}&pageType=${options.pageType}`;
    if (options.sectionId !== undefined)
      query = `${query}&sectionId=${options.sectionId}`;
    if (options.unitId !== undefined)
      query = `${query}&unitId=${options.unitId}`;
    if (options.bookId !== undefined)
      query = `${query}&bookId=${options.bookId}`;
    if (options.bookTypeId !== undefined)
      query = `${query}&bookTypeId=${options.bookTypeId}`;
    if (options.booksetId !== undefined)
      query = `${query}&booksetId=${options.booksetId}`;
    if (options.resourceId !== undefined)
      query = `${query}&resourceId=${options.resourceId}`;
    if (options.isHidden !== undefined)
      query = `${query}&isHidden=${options.isHidden.toString()}`;
    if (options.isTrash !== undefined)
      query = `${query}&isTrash=${options.isTrash.toString()}`;

    const route = `pageNumber${query ? `?${query.slice(1)}` : ``}`;

    const {
      data: { pagesNumbers }
    } = await axios.get(route, configs);

    dispatch({
      type: FETCH_PAGES_NUMBER,
      payload: pagesNumbers
    });

    if (callback) callback();
  };

// check video status
export const checkVideoStatus = (videoId) => async (dispatch, getStore) => {
  const {
    data: { status, _thumbnail }
  } = await axios.get(`video/checkStatus/${videoId}`);

  return { status, _thumbnail };
};

/**
 * PLAYGROUND
 */

//add playground
export const addPlayground = (name) => {
  return { type: ADD_PLAYGROUND, payload: name };
};

//delete playground
export const deletePlayground = (index) => {
  return { type: DELETE_PLAYGROUND, payload: { index } };
};

export const addPlaygroundLevel = (level) => {
  return { type: ADD_PLAYGROUNDLEVEL, payload: level };
};

/**
 * HEALTH CHECK / QUIZ
 */

// Submit quiz results to backend
export const submitQuizResults =
  (quizResults, callback) => async (dispatch, getStore) => {
    try {
      const { data } = await axios.post(`quiz/results`, quizResults);

      if (callback) callback(data);
      return data;
    } catch (error) {
      console.error("Failed to submit quiz results:", error);
      dispatch({
        type: SET_ERROR,
        payload: `Failed to submit quiz results: ${error.message}`
      });
      throw error;
    }
  };
